'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Sword, Star, Wind } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function EpeesPage() {
  const epeesCards = [
    { number: 'As', name: 'As d\'Épées', meaning: 'Yeni fikirler, zihinsel netlik' },
    { number: '2', name: 'Deux d\'Épées', meaning: 'Karar verme, denge' },
    { number: '3', name: 'Trois d\'Épées', meaning: 'Kalp kırıklığı, acı' },
    { number: '4', name: 'Quatre d\'Épées', meaning: 'Dinlenme, meditasyon' },
    { number: '5', name: 'Cinq d\'Épées', meaning: 'Çatışma, yenilgi' },
    { number: '6', name: 'Six d\'Épées', meaning: 'Geçiş, seyahat' },
    { number: '7', name: 'Sept d\'Épées', meaning: 'Aldatma, hırsızlık' },
    { number: '8', name: 'Huit d\'Épées', meaning: 'Kısıtlama, tuzak' },
    { number: '9', name: 'Neuf d\'Épées', meaning: 'Kabus, endişe' },
    { number: '10', name: 'Dix d\'Épées', meaning: 'Son, yıkım' },
    { number: 'V', name: 'Valet d\'Épées', meaning: 'Meraklı genç, haber' },
    { number: 'C', name: 'Cavalier d\'Épées', meaning: 'Acele, saldırganlık' },
    { number: 'D', name: 'Dame d\'Épées', meaning: 'Keskin zeka, bağımsızlık' },
    { number: 'R', name: 'Roi d\'Épées', meaning: 'Otorite, zihinsel güç' }
  ]

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-gray-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-gray-500">/</span>
            <span className="text-sm">Épées</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Minör Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800/30 to-slate-700/30 rounded-xl p-8 mb-8 border border-gray-600/30">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-slate-700 rounded-full flex items-center justify-center">
              <Sword className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-slate-100">Épées (Kılıçlar)</h1>
              <p className="text-xl text-gray-400">Hava Elementi - Zihin, Düşünce, İletişim</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            Marseille geleneğinde hava elementini temsil eden Épées takımı, zihin, düşünce ve iletişim alanlarını simgeler.
            Bu kartlar zihinsel süreçler, çatışma ve adalet konularında rehberlik sunar.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Épées Kartları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {epeesCards.map((card, index) => {
              const cardSlug = card.number === 'As' ? 'as' :
                             card.number === 'V' ? 'valet' :
                             card.number === 'C' ? 'cavalier' :
                             card.number === 'D' ? 'dame' :
                             card.number === 'R' ? 'roi' :
                             card.number === '2' ? 'deux' :
                             card.number === '3' ? 'trois' :
                             card.number === '4' ? 'quatre' :
                             card.number === '5' ? 'cinq' :
                             card.number === '6' ? 'six' :
                             card.number === '7' ? 'sept' :
                             card.number === '8' ? 'huit' :
                             card.number === '9' ? 'neuf' :
                             card.number === '10' ? 'dix' : card.number.toLowerCase();

              // Card visual representations
              const getCardVisual = (number) => {
                if (number === 'As') return '🗡️';
                if (number === 'V') return '🧑';
                if (number === 'C') return '🏇';
                if (number === 'D') return '👸';
                if (number === 'R') return '👑';
                return '⚔️';
              };

              return (
                <Link
                  key={card.number}
                  href={`/about/tarot/systems/marseille/minor-arcana/epees/${cardSlug}`}
                  className="bg-slate-800 rounded-lg p-6 border border-slate-700 hover:border-gray-500/50 transition-colors group cursor-pointer"
                >
                  <div className="text-center">
                    {/* Card Visual */}
                    <div className="w-20 h-28 mx-auto mb-4 bg-gradient-to-b from-gray-100 to-slate-50 rounded-lg border border-gray-300 flex flex-col items-center justify-center relative group-hover:scale-105 transition-transform shadow-lg">
                      <div className="text-2xl mb-1">{getCardVisual(card.number)}</div>
                      <div className="text-xs font-bold text-gray-800">{card.number}</div>
                      <div className="absolute top-1 left-1 text-gray-600">
                        <Sword className="h-2 w-2" />
                      </div>
                      <div className="absolute top-1 right-1 text-gray-600">
                        <Sword className="h-2 w-2" />
                      </div>
                      <div className="absolute bottom-1 left-1 text-gray-600">
                        <Wind className="h-2 w-2" />
                      </div>
                      <div className="absolute bottom-1 right-1 text-gray-600">
                        <Wind className="h-2 w-2" />
                      </div>
                    </div>

                    <h3 className="font-semibold text-slate-100 mb-2 text-sm group-hover:text-gray-400 transition-colors">{card.name}</h3>
                    <p className="text-xs text-slate-400">{card.meaning}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Suit Information */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6 flex items-center gap-2">
            <Wind className="h-6 w-6 text-gray-400" />
            Épées Takımının Özellikleri
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-slate-100 mb-3">Element ve Enerji</h3>
              <p className="text-slate-300 mb-4">Hava - Zihin, düşünce, iletişim ve çatışma</p>

              <h3 className="font-semibold text-slate-100 mb-3">Temsil Ettiği Alanlar</h3>
              <ul className="text-slate-300 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  Zihinsel süreçler
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  İletişim ve tartışma
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  Çatışma ve mücadele
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  Adalet ve hakikat
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-slate-100 mb-3">Marseille Tradisyonu</h3>
              <p className="text-slate-300 mb-4">
                Épées, Marseille tarotunda hava elementini ve zihinsel gücü temsil eder.
                Geleneksel çizimler, keskin kılıçlar şeklindedir.
              </p>

              <h3 className="font-semibold text-slate-100 mb-3">Court Kartları</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Valet:</strong> Meraklı zihin</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Cavalier:</strong> Hızlı aksiyon</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Dame:</strong> Keskin zeka</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-slate-300"><strong className="text-slate-200">Roi:</strong> Zihinsel otorite</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/minor-arcana/batons"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Bâtons
          </Link>

          <Link
            href="/about/tarot/systems/marseille/minor-arcana/coupes"
            className="inline-flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Coupes
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  )
}